import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { brandTextColor } from "../../../../config/theme";
import { getTimeAgo, type TimeTranslations } from "../../../../utils/getDate";
import { useVehiclesSocketStore } from "../../../../store/admin/vehiclesSocketStore";
import { useCopyLocation } from "../../../../hooks/useCopyLocation";
import { resolveVehicleModel } from "../../../../config/vehicleModels";
import UnitVehicleAvatar from "../../../../components/vehicles/UnitVehicleAvatar";
import { useVehicleGarage } from "../../../../features/vehicles/pointerModel";
import { useDragOffset } from "../../../../hooks/useDragOffset";
import { liveSpeedKmh, readEngine } from "../../../../Layouts/lite/components/Sidebar/engineDisplay";
import { TONE_COLOR, connectionTone } from "../../../../Layouts/lite/components/Sidebar/indicators";
import { UNIT_STATUS_COLORS as STATUS_COLORS, ENGINE_COLORS } from "../../../../constants/unitColors";

interface UnitInfoModalProps {
  unit: any;
  onClose: () => void;
  /** Minimizar: la ficha vive en el botón "Siguiendo" del mapa. */
  onMinimize: () => void;
  /** Se abrió desde la pastilla: crece desde ella en vez de aparecer en su lugar. */
  fromPill?: boolean;
  isDarkMode?: boolean;
}


// "Lámpara mágica" hacia la pastilla de la unidad (arriba a la izquierda): al
// minimizar, la ficha se encoge y vuela hasta ella, y la pastilla late al
// recibirla; al abrir desde la pastilla hace el camino inverso. Sin eso, a
// primera vista no se sabía a dónde se fue. Sólo transform/opacity (fluido);
// el genio de macOS deforma la ventana, en web eso es caro: se aproxima con
// escala no uniforme (primero se angosta, luego viaja). Con "reducir
// movimiento" no hay vuelo.
export const MINIMIZE_EVENT = "dumax:minimize-unit-card";
const GENIE_MS = 340;
const GENIE_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

function genieFrames(card: HTMLElement): Keyframe[] | null {
  const pill = document.querySelector<HTMLElement>("[data-unit-pill]");
  const c = card.getBoundingClientRect();
  const host = (pill ?? card.offsetParent ?? card).getBoundingClientRect();
  // Sin pastilla (se dejó de seguir), la esquina superior izquierda del mapa.
  const tx = pill ? host.left + host.width / 2 : host.left + 24;
  const ty = pill ? host.top + host.height / 2 : host.top + 20;
  const dx = tx - (c.left + c.width / 2);
  const dy = ty - (c.top + c.height / 2);
  return [
    { transform: "none", opacity: 1 },
    { transform: `translate(${dx * 0.35}px, ${dy * 0.25}px) scale(0.6, 0.45)`, opacity: 0.9, offset: 0.45 },
    { transform: `translate(${dx}px, ${dy}px) scale(0.1, 0.06)`, opacity: 0 },
  ];
}

// Ficha de la unidad: una ventana compacta anclada arriba a la derecha del
// mapa (bajo la lupa), no un panel de 360 px con íconos subrayados. Se abre al
// tocar el marcador; "—", Esc o tocar el marcador otra vez la MINIMIZAN al
// botón "Siguiendo" de arriba a la izquierda (que ya representa a esa unidad;
// tocarlo la vuelve a abrir); la X la cierra. Ya no se
// cierra sola con cualquier clic en la lista o el mapa.
export default function UnitInfoModal({
  unit: initialUnit,
  onClose,
  onMinimize,
  fromPill = false,
  isDarkMode = false,
}: UnitInfoModalProps) {
  const { t } = useTranslation("lite");

  const fresh = useVehiclesSocketStore((s) => {
    const idx = s.vehiclesMap.get(initialUnit?.id);
    return idx !== undefined ? s.vehicles[idx] : undefined;
  });
  const isLoadingAddress = useVehiclesSocketStore((s) => !!s.loadingAddressDeviceIds[initialUnit?.id]);
  const requestAddressForDevice = useVehiclesSocketStore((s) => s.requestAddressForDevice);
  useVehiclesSocketStore((s) => s.timeTick);

  const unit = fresh || initialUnit;
  const model = resolveVehicleModel(unit?.vehicle?.pointerModel, unit?.vehicle?.bodyType, unit?.vehicle?.type);
  const copyLocation = useCopyLocation(unit?.latitude, unit?.longitude);

  // La dirección se pide al abrir (MapView) y se refresca a lo más cada 10 s
  // mientras la unidad se mueve: cada pedido es geocodificación pagada.
  const lastAddressRequestRef = useRef(Date.now());
  useEffect(() => {
    if (!unit?.id || unit.latitude == null || unit.longitude == null) return;
    const now = Date.now();
    if (now - lastAddressRequestRef.current < 10000) return;
    lastAddressRequestRef.current = now;
    requestAddressForDevice(unit.id);
  }, [unit?.id, unit?.latitude, unit?.longitude, requestAddressForDevice]);

  useEffect(() => {
    // Esc minimiza, no cierra: la unidad sigue a un toque.
    // Con el garaje abierto, Esc es suyo (cerrarlo), no de la ficha.
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && !useVehicleGarage.getState().target && minimizeRef.current();
    const onEvt = () => minimizeRef.current();
    window.addEventListener("keydown", onKey);
    window.addEventListener(MINIMIZE_EVENT, onEvt);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(MINIMIZE_EVENT, onEvt);
    };
  }, []);

  const rootRef = useRef<HTMLDivElement>(null);
  const leavingRef = useRef(false);
  // Se arrastra desde el encabezado para acomodarla junto a la vista de manejo
  // (se encimaban, Daniel 29-oct). Sólo en escritorio: en el teléfono es una hoja abajo.
  const [desktop] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const drag = useDragOffset("unitCard.offset", desktop, rootRef);
  const minimize = () => {
    const el = rootRef.current;
    if (leavingRef.current) return;
    const frames = el && !reduceMotion() ? genieFrames(el) : null;
    if (!el || !frames) return onMinimize();
    leavingRef.current = true;
    el.animate(frames, { duration: GENIE_MS, easing: GENIE_EASE, fill: "forwards" }).onfinish = () => {
      onMinimize();
      document
        .querySelector<HTMLElement>("[data-unit-pill]")
        ?.animate([{ transform: "scale(1)" }, { transform: "scale(1.1)" }, { transform: "scale(1)" }], {
          duration: 300,
          easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        });
    };
  };
  const minimizeRef = useRef(minimize);
  minimizeRef.current = minimize;

  // Abierta desde la pastilla: el mismo camino al revés.
  useEffect(() => {
    const el = rootRef.current;
    if (!fromPill || !el || reduceMotion()) return;
    const frames = genieFrames(el);
    if (frames) el.animate([...frames].reverse(), { duration: GENIE_MS - 40, easing: GENIE_EASE });
    // Sólo al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: t("time.noData"),
      rightNow: t("time.rightNow"),
      min: t("time.min"),
      hour: t("time.hour"),
      hours: t("time.hours"),
      day: t("time.day"),
      days: t("time.days"),
      month: t("time.month"),
      months: t("time.months"),
      and: t("time.and"),
    }),
    [t],
  );

  const c = {
    panel: isDarkMode ? "bg-dark-surface/95 border-dark-border" : "bg-white/95 border-gray-200",
    text: isDarkMode ? "text-gray-100" : "text-gray-900",
    sub: isDarkMode ? "text-gray-400" : "text-gray-500",
    tile: isDarkMode ? "bg-white/[0.04]" : "bg-gray-50",
    line: isDarkMode ? "border-white/5" : "border-gray-100",
    btn: isDarkMode
      ? "border-dark-border text-gray-200 hover:bg-white/5"
      : "border-gray-200 text-gray-700 hover:bg-gray-50",
    icon: isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100",
  };

  const status = unit.status as string;
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.offline;
  const speed = liveSpeedKmh(unit.speed, status);
  const engine = readEngine(unit.speed, status, unit.engineStatus).tone;
  const freshness = connectionTone(unit.lastReport?._seconds, Date.now());
  const driver = unit.driver?.name && unit.driver.name !== "Sin asignar" ? unit.driver : null;
  const name = unit.vehicle?.name || unit.imei || unit.id;
  const hasAddress = unit.address && unit.address !== "N/A";

  const tiles: { label: string; value: string; color?: string }[] = [
    {
      label: t("unitModal.speed"),
      value: speed != null ? `${Math.round(speed)} km/h` : t("unitModal.noData"),
    },
    {
      label: t("unitModal.ignitionLabel"),
      value:
        engine === "on" ? t("unitModal.ignition.on") : engine === "off" ? t("unitModal.ignition.off") : t("unitModal.noData"),
      color: ENGINE_COLORS[engine],
    },
    {
      label: t("unitModal.lastReport"),
      value: getTimeAgo(unit.lastReport?._seconds, timeTranslations),
      color: freshness === "gray" ? undefined : TONE_COLOR[freshness],
    },
    {
      label: t("unitModal.odometer"),
      value: unit.odometer != null ? `${Math.round(unit.odometer).toLocaleString()} km` : t("unitModal.noData"),
    },
  ];

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={name}
      // Arriba a la derecha, bajo la lupa y pantalla completa (10 + 40 + 10 px);
      // en el teléfono, como hoja abajo. Crece desde su esquina (origin).
      className={`absolute z-30 left-2 right-2 bottom-2 md:left-auto md:bottom-auto md:right-[10px] md:top-[60px] md:w-[320px] max-h-[calc(100%-80px)] overflow-y-auto rounded-xl border shadow-xl backdrop-blur-sm origin-bottom md:origin-top-right ${fromPill ? "" : "motion-safe:animate-scaleIn"} ${c.panel}`}
      style={drag.style}
    >
      {/* Encabezado: quién es y qué está haciendo, en una mirada. Con modelo,
          su carátula encabeza la ficha (como la tarjeta del auto en un juego). */}
      <div className={`flex items-start gap-3 p-3 pb-2 ${desktop ? "cursor-grab active:cursor-grabbing select-none touch-none" : ""}`} {...drag.handlers}>
        {/* Carátula compacta junto al nombre (como avatar), con lápiz para
            cambiar el vehículo en el garaje; con "Clásico", el punto de
            estado y el lápiz igual (si no, ya no se podía salir del clásico). */}
        <UnitVehicleAvatar
          vehicleId={unit.vehicle_id ?? ""}
          name={unit.vehicle?.name || unit.imei || unit.id}
          pointerModel={unit.vehicle?.pointerModel}
          bodyType={unit.vehicle?.bodyType}
          unitType={unit.vehicle?.type}
          editable={!!unit.vehicle_id}
          fallback={<span className={`w-16 h-11 rounded-lg flex items-center justify-center ring-1 ring-inset ${isDarkMode ? "bg-white/[0.04] ring-white/5" : "bg-gray-50 ring-black/5"}`}><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColor }} /></span>}
        />
        <div className="min-w-0 flex-1">
          <h2 className={`font-mono font-semibold text-sm truncate ${c.text}`}>
            {model && <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: statusColor }} />}
            {name}
          </h2>
          <p className="text-xs mt-0.5" style={{ color: status === "driving" ? brandTextColor : statusColor }}>
            {t(`unitsTable.status.${status}`, t("unitsTable.status.offline"))}
            {speed != null && speed >= 1 && <span className={c.sub}> · {Math.round(speed)} km/h</span>}
          </p>
        </div>
        <div className="-mr-1 -mt-1 flex items-center">
          <button
            type="button"
            onClick={minimize}
            aria-label={t("unitModal.minimize", "Minimizar")}
            title={t("unitModal.minimize", "Minimizar")}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${c.icon}`}
          >
            <i className="ri-subtract-line text-lg" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("unitsTable.close", "Cerrar")}
            title={t("unitsTable.close", "Cerrar")}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${c.icon}`}
          >
            <i className="ri-close-line text-lg" aria-hidden />
          </button>
        </div>
      </div>

      {/* Dónde está: la dirección, que es lo que se pregunta primero. */}
      <div className={`px-3 pb-3 flex items-start gap-2 text-xs ${c.sub}`}>
        <i className="ri-map-pin-line text-sm leading-4 mt-px" aria-hidden />
        {isLoadingAddress && !hasAddress ? (
          <span className="animate-pulse">{t("unitModal.loadingAddress")}</span>
        ) : (
          <span className={`leading-4 ${hasAddress ? c.text : ""}`}>
            {hasAddress ? unit.address : t("unitModal.noAddress")}
            {unit.reference && <span className={`block mt-0.5 ${c.sub}`}>{unit.reference}</span>}
          </span>
        )}
      </div>

      {/* Datos en cuadrícula 2×2: etiqueta chica arriba, valor grande abajo. */}
      <div className="px-3 grid grid-cols-2 gap-1.5">
        {tiles.map((x) => (
          <div key={x.label} className={`rounded-lg px-2.5 py-2 ${c.tile}`}>
            <p className={`text-[11px] ${c.sub}`}>{x.label}</p>
            <p className={`text-sm font-semibold tabular-nums truncate ${x.color ? "" : c.text}`} style={x.color ? { color: x.color } : undefined}>
              {x.value}
            </p>
          </div>
        ))}
      </div>

      {/* Conductor en una línea; el teléfono se puede tocar. */}
      <div className={`mx-3 mt-3 pt-3 border-t flex items-center gap-2 text-xs ${c.line}`}>
        <i className={`ri-user-line text-sm ${c.sub}`} aria-hidden />
        <span className={`truncate ${driver ? c.text : c.sub}`}>{driver ? driver.name : t("unitModal.noDriver")}</span>
        {driver?.phone && (
          <a href={`tel:${driver.phone}`} className="ml-auto shrink-0 text-primary-500 hover:underline tabular-nums">
            {driver.phone}
          </a>
        )}
      </div>

      <div className="p-3 flex items-center gap-2">
        {copyLocation.available && (
          <button
            type="button"
            onClick={() => void copyLocation.copy()}
            aria-live="polite"
            title={t("quickActions.copyLocationTip")}
            className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-xs font-medium transition-colors active:translate-y-px ${
              copyLocation.state === "copied" ? "border-primary-500 text-primary-500" : c.btn
            }`}
          >
            <i className={`${copyLocation.state === "copied" ? "ri-check-line" : "ri-file-copy-line"} text-sm`} aria-hidden />
            {copyLocation.state === "copied" ? t("quickActions.copied") : t("unitsTable.copy")}
          </button>
        )}
        <span className={`ml-auto text-[11px] tabular-nums ${c.sub}`} title={t("unitModal.coordinates")}>
          {Number(unit.latitude).toFixed(5)}, {Number(unit.longitude).toFixed(5)}
        </span>
      </div>
    </div>
  );
}
